import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Project, ProjectsListRequest } from "@/api";
import getTbiApi from "@/api/api-v1";

// SINGLE

export const projectIsFailed = (project: Project | undefined) => {
  if (!project) return true;

  if (projectIsLoading(project)) {
    console.warn("Project is still loading, cannot determine if it has failed");
    return false;
  }
  return project.themes.some(({ maps }) =>
    maps?.some(({ calculationSuccesful }) => !calculationSuccesful)
  );
};

export const projectIsLoading = (project: Project | undefined) => {
  if (!project) return true;
  return project.themes.some(({ maps }) => maps?.some(({ calculated }) => !calculated));
};

const getProjectQuery = (slug: string) =>
  getTbiApi().getQueryOptions({
    queryKey: ["project-detail-raw", slug],
    queryFn: async () => {
      return await getTbiApi().ProjectsApi.projectsRetrieve({
        slug,
      });
    },
    refetchInterval: (query) => (projectIsLoading(query.state.data) ? 5000 : false),
  });

export const useProjectQuery = (slug: string) => useQuery(getProjectQuery(slug));

// LIST

const getProjectListQuery = (args: ProjectsListRequest) =>
  getTbiApi().getQueryOptions({
    queryKey: ["project-list", args],
    queryFn: async () => {
      return await getTbiApi().ProjectsApi.projectsList(args);
    },
  });

export const useProjectListQuery = (args: ProjectsListRequest) =>
  useQuery(getProjectListQuery(args));

export const useInvalidateProjectQueries = () => {
  const queryClient = useQueryClient();
  return async (slug: string) => {
    await queryClient.invalidateQueries({
      queryKey: getProjectQuery(slug).queryKey,
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getProjectListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};

export const useUpdateProjectQueries = () => {
  const queryClient = useQueryClient();
  return async (project: Project) => {
    if (project.slug)
      await queryClient.setQueryData(getProjectQuery(project.slug).queryKey, project);
    await queryClient.invalidateQueries({
      queryKey: getProjectListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};
